import {canViewImage} from './images';
import {runArchive} from '../home_media_ui/archive';
import { resolveMediaURL } from '../home_media_ui/direct';
import { API_URL } from '../api';
import { getAuthToken } from '../auth';

async function storageFetch(url, options) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timer = setTimeout(abort, 90000);
  options?.signal?.addEventListener('abort', abort, {once:true});
  if (options?.signal?.aborted) controller.abort();
  try { return await fetch(url, { ...options, signal:controller.signal, redirect: 'error' }); }
  catch (error) {
    if (options?.signal?.aborted) throw error;
    throw new Error('Не удалось получить ответ хранилища. Повторите попытку. Если ошибка сохраняется, сообщите менеджеру, какую папку или файл вы открывали.');
  }
  finally {clearTimeout(timer); options?.signal?.removeEventListener('abort',abort);}
}

export const SIZE_LIMIT = 2 * 1024 ** 3;
export function credentials(share = '') { return { share, auth: window.location.pathname.startsWith('/disk/') ? '' : (getAuthToken() || '') }; }
export function headers(ctx) {
  return { ...(ctx.auth ? { Authorization: `Bearer ${ctx.auth}` } : {}), ...(ctx.share ? { 'X-Storage-Share': ctx.share } : {}) };
}
export async function request(path, { method = 'GET', data, ctx = credentials(), signal } = {}) {
  const res = await storageFetch(`${API_URL}/api/storage/${path}`, { method, signal, credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
    headers: { ...headers(ctx), ...(data !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    body: data !== undefined ? JSON.stringify(data) : undefined });
  if (!res.ok) {
    let body; try { body = await res.json(); } catch { body = null; }
    if(res.status===401&&body?.code==='materials_customer_login_required'&&ctx.share&&window.location.pathname.startsWith('/disk/')){
      try{
        const target=new URL(body.url);
        if(target.protocol==='https:'&&!target.username&&!target.password&&!target.search&&!target.hash
          &&target.pathname===`/materials/folder/${ctx.share}`){
          window.location.replace(target.href);
        }
      }catch{/* Keep the ordinary error if the server's login address is invalid. */}
    }
    const error = new Error(typeof body?.detail === 'string' ? body.detail : body ? JSON.stringify(body) : (res.status >= 500 ? `Основной сервер не смог обработать запрос (${res.status}). Повторите попытку или сообщите менеджеру.` : `Ошибка сервера (${res.status})`));
    error.status = res.status; throw error;
  }
  if (!res.headers.get('content-type')?.toLowerCase().includes('application/json')) {
    const error = new Error('API хранилища вернул страницу вместо данных. Проверьте адрес API и маршрут /api/storage/ в nginx.');
    error.status = 502; throw error;
  }
  try { return await res.json(); }
  catch { const error = new Error('API хранилища вернул некорректный JSON. Повторите запрос или сообщите администратору.'); error.status = 502; throw error; }
}
export function sendPart(id, index, blob, ctx, onProgress, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/storage/uploads/${id}/parts/${index}/`);
    xhr.timeout = 600000; // Слабая связь: разрешаем долгую передачу, пока идут байты.
    Object.entries(headers(ctx)).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    let idle;
    const abort = () => xhr.abort();
    const resetIdle = () => { clearTimeout(idle); idle=setTimeout(()=>xhr.abort(),60000); };
    const fail = (message, status = 0) => { const error = new Error(message); error.status = status; reject(error); };
    xhr.upload.onprogress = (e) => { resetIdle(); if (e.lengthComputable) onProgress(Math.min(1, e.loaded / e.total)); };
    xhr.onload = () => {
      let data; try { data = JSON.parse(xhr.responseText); } catch { data = {}; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else fail(typeof data.detail === 'string' ? data.detail : `Ошибка загрузки (${xhr.status})`, xhr.status);
    };
    xhr.onerror = () => fail('Загрузка приостановлена: сеть или CORS. После восстановления доступа можно продолжить.');
    xhr.ontimeout = () => fail('Время ожидания истекло. Можно продолжить загрузку.');
    xhr.onabort = () => fail('Загрузка приостановлена');
    xhr.onloadend = () => {clearTimeout(idle);signal?.removeEventListener('abort', abort);};
    signal?.addEventListener('abort', abort);
    if (signal?.aborted) { fail('Загрузка приостановлена'); return; }
    resetIdle();
    const form = new FormData(); form.append('chunk', blob, 'part'); xhr.send(form);
  });
}
// HOME_MEDIA_V2_DOWNLOAD: every action obtains a fresh permission from the main API.
async function mediaTicket(node, ctx, {preview = false, thumbnail = false, version, signal} = {}) {
  const query = new URLSearchParams();
  if (preview) query.set('preview', '1');
  if (thumbnail) query.set('preview', '1'); // Never ask home to decode/resize images.
  if (version) query.set('version', version);
  const ticket = await request(`nodes/${node.id}/ticket/?${query}`, {ctx, signal});
  return {url: await resolveMediaURL(API_URL, ticket.url, {preview: preview || thumbnail, signal}), query};
}
export async function imageSource(node, ctx, {signal} = {}) {
  if (!canViewImage(node)) throw new Error('Этот формат доступен для скачивания. Предпросмотр поддерживает только изображения.');
  const ticket = await mediaTicket(node, ctx, {preview: true, signal,
    version: ctx.share ? undefined : node.version});
  return ticket.url;
}
export async function content(node, ctx, options = {}) {
  const ticket = await mediaTicket(node, ctx, options);
  let response;
  try {
    response = await storageFetch(ticket.url, {signal: options.signal, credentials: 'omit', referrerPolicy: 'no-referrer'});
    if (response.status === 401 || response.status === 403) {
      const renewed = await mediaTicket(node, ctx, options);
      response = await storageFetch(renewed.url, {signal: options.signal, credentials: 'omit', referrerPolicy: 'no-referrer'});
    }
  } catch (error) {
    if (error.status || options.signal?.aborted || error.name === 'AbortError') throw error;
    // Preview compatibility if the media domain cannot be read through browser CORS.
    // Main credentials are sent ONLY to the main API, which checks access again.
    response = await storageFetch(`${API_URL}/api/storage/nodes/${node.id}/content/?${ticket.query}`, {
      signal: options.signal, headers: headers(ctx), credentials: 'omit', referrerPolicy: 'no-referrer'});
  }
  if (!response.ok) {
    const error = new Error('Файл временно недоступен. Обновите папку и повторите.');
    error.status = response.status;
    throw error;
  }
  return response.blob();
}
export async function download(node, ctx, version, signal) {
  const ticket = await mediaTicket(node, ctx, {version, signal});
  if (signal?.aborted) throw new DOMException('Передача отменена', 'AbortError');
  // Navigation streams the download from home; no multi-gigabyte Blob in browser memory.
  const anchor = document.createElement('a');
  anchor.href = ticket.url; anchor.rel = 'noopener noreferrer'; anchor.referrerPolicy = 'no-referrer';
  document.body.appendChild(anchor); anchor.click(); anchor.remove();
}
export function uuid() {
  const a = new Uint8Array(16); crypto.getRandomValues(a); a[6] = (a[6] & 15) | 64; a[8] = (a[8] & 63) | 128;
  const h = [...a].map(v => v.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
}
export async function hash(buffer) {
  if (!crypto.subtle) throw new Error('Для загрузки нужен HTTPS и браузер с Web Crypto. Обновите браузер.');
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256', buffer))].map(v => v.toString(16).padStart(2,'0')).join('');
}
export const bytes = value => value < 1024 ? `${value} Б` : value < 1024 ** 2 ? `${(value / 1024).toFixed(1)} КБ` : `${(value / 1024 ** 2).toFixed(1)} МБ`;

export function readBuffer(blob) {
  if (blob.arrayBuffer) return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Не удалось прочитать файл')); reader.readAsArrayBuffer(blob);
  });
}

export function downloadArchive(ids, ctx, options = {}) {
  return runArchive(API_URL, ctx.auth, {scope: 'storage', ids}, {...options, share: ctx.share});
}
